import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Calculator, Info, Layers, TrendingUp, Zap, BarChart3, PieChart as PieChartIcon, Settings, ChevronDown, ChevronUp, AlertTriangle, FlaskConical, Save, FileText, Loader2, FileSpreadsheet, User, Users, Clock, BookOpen, Pin, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { KPITile } from '../components/dashboard/KPITile';
import { MeasureContributionChart, MeasureSharePieChart } from '../components/charts/MeasureContributionChart';
import { LEIMeasureInfo } from '../components/LEIMeasureInfo';
import { LEICalculatorDrawer } from '../components/LEICalculatorDrawer';
import { AnalysisModeToggle } from '../components/AnalysisModeToggle';
import { SaveScenarioDialog } from '../components/SaveScenarioDialog';
import CohortSelector from '../components/CohortSelector';
import BoardReadyButton from '../components/BoardReadyButton';
import BenchmarkComparison from '../components/BenchmarkComparison';
import NarrativeCard from '../components/analysis/NarrativeCard';
import LeiImpactCard from '../components/LeiImpactCard';
import { getLEIMeasures, getKPIs, calculatePortfolio } from '../lib/api';
import { formatCurrency, getCurrencySymbol } from '../lib/utils';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { MaturitySliderWithGuide } from '../components/MaturitySliderWithGuide';
import { TransferFactorsCard, TransferRateResultsCard } from '../components/TransferFactorsCard';
import { ExecutionRiskCollapsible } from '../components/ExecutionRiskFactors';
import { toast } from 'sonner';
import api from '../lib/api';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

export const PortfolioAnalysisPage = () => {
  const { setResults: setGlobalResults, setConfiguration: setGlobalConfig, currency, setNarrative } = useApp();
  const { profileDefaults, hasProfileDefaults, viewAsOrg } = useAuth();
  
  const [leiMeasures, setLEIMeasures] = useState([]);
  const [kpis, setKPIs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [results, setResults] = useState(null);
  const [resultsStale, setResultsStale] = useState(false); // Track if inputs changed
  const [bulkValue, setBulkValue] = useState(2.00);
  const [showDampeningDetails, setShowDampeningDetails] = useState(true);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [analysisMode, setAnalysisMode] = useState('simple'); // 'simple', 'advanced', 'import'
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState(null);
  const [profileDefaultsApplied, setProfileDefaultsApplied] = useState(false);
  // Maturity Assessment auto-sync — mirrors Single Measure UX.
  const navigate = useNavigate();
  const [latestMaturityAssessment, setLatestMaturityAssessment] = useState(null);
  const [maturityAssessmentApplied, setMaturityAssessmentApplied] = useState(false);
  const [maturityOverridden, setMaturityOverridden] = useState(false);
  const [isDetailedBreakdownExpanded, setIsDetailedBreakdownExpanded] = useState(false);  // Collapsed by default
  const [isImpactAdjustmentsExpanded, setIsImpactAdjustmentsExpanded] = useState(false);  // Collapsed by default
  const [importedBaselines, setImportedBaselines] = useState(null);
  const [isLoadingBaselines, setIsLoadingBaselines] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const configRef = useRef(null);
  const resultsRef = useRef(null);
  const configSectionRef = useRef(null);  // For scrolling to config section
  // When the user lands here from the LEI Reference "Model the fix"
  // CTA, location.state carries the pillar to focus on. We preserve
  // it in component state so it survives re-renders + can be cleared
  // once the user dismisses the focus banner.
  const location = useLocation();
  const [focusPillar, setFocusPillar] = useState(location.state?.focusPillar || null);
  const [focusPillarLabel, setFocusPillarLabel] = useState(location.state?.focusPillarLabel || null);
  const [focusPillarApplied, setFocusPillarApplied] = useState(false);
  // PE-weak-link handoff — triggered from PEHealthPage's
  // "Model this fix" CTA. The page itself is LEI-measure-driven
  // (PE components are downstream), so this state powers a small
  // contextual banner that tells the CLO which PE component they
  // came here to lift. No pre-filling of measures — that's a
  // future enhancement once we have a PE→LEI mapping.
  const [focusPEComponentLabel] = useState(
    location.state?.focusReason === 'pe-weak-link'
      ? location.state?.focusPEComponentLabel
      : null,
  );
  const [focusPELiftPoints] = useState(
    location.state?.focusPELiftPoints || 15,
  );
  // Maturity-weak-link handoff — same soft-banner pattern as PE.
  // Maturity dimensions (e.g. "Evaluation & Measurement") don't
  // map 1:1 to LEI measures either, so this surfaces context
  // without pre-filling.
  const [focusMaturityDimension] = useState(
    location.state?.focusReason === 'maturity-weak-link'
      ? location.state?.focusMaturityDimension
      : null,
  );
  const [focusMaturityLiftPoints] = useState(
    location.state?.focusMaturityLiftPoints || 1,
  );
  // ROE-weak-link handoff — same soft-banner pattern. ROE metric
  // is a signed variance in pts so the headline reads "Modeling an
  // ROE {Metric} variance close (+15pts)".
  const [focusROEMetric] = useState(
    location.state?.focusReason === 'roe-weak-link'
      ? location.state?.focusROEMetric
      : null,
  );
  const [focusROELiftPoints] = useState(
    location.state?.focusROELiftPoints || 15,
  );
  // ROI-weak-link handoff — targets a specific scenario rather than
  // a metric. Banner nudges the CLO to adjust L&D measures tied to
  // that scenario.
  const [focusROIScenarioName] = useState(
    location.state?.focusReason === 'roi-weak-link'
      ? location.state?.focusROIScenarioName
      : null,
  );
  const [focusROILiftPoints] = useState(
    location.state?.focusROILiftPoints || 15,
  );
  // Pin-to-Quarterly-Deck state. Supports both:
  //   1. Manual click on the "Pin to Quarterly Deck" button (always
  //      available once results exist)
  //   2. URL-driven auto-pin — when the user lands via a "Model the
  //      fix" CTA with `?pin=1` or `location.state.autoPin`, the
  //      page silently fires one pin as soon as `results` arrive.
  //      This closes the CLO's "weak-link → pin" loop to a single
  //      click on the health page. Guarded by `autoPinFired` so
  //      re-renders don't double-pin.
  const [isPinning, setIsPinning] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const autoPinFiredRef = useRef(false);
  const searchParams = new URLSearchParams(location.search || '');
  const autoPinRequested =
    searchParams.get('pin') === '1' || location.state?.autoPin === true;
  // Refs to scroll into view once the page has rendered the focused
  // pillar group. Keyed by pillar string for O(1) lookup.
  const pillarRefs = useRef({});
  
  // Scroll to config section
  const scrollToConfig = () => {
    configSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Portfolio configuration - initialize from profile defaults
  const [measureInputs, setMeasureInputs] = useState([]);
  const [config, setConfig] = useState(() => {
    // Will be updated with profile defaults in useEffect
    return {
      kpi_id: 'productivity',
      elasticity_scenario: 'moderate',
      intervention_cost: 500000,
      time_horizon_months: 12,
      current_maturity: 50,
      dampening_factors: {
        adoption_speed: 0.3,       // Risk likelihood (0-1): lower = less risk
        leadership_alignment: 0.2, // Risk likelihood (0-1): lower = less risk
        operational_readiness: 0.25, // Risk likelihood (0-1): lower = less risk
      },
      headcount: 1000,
      revenue_per_employee: 150000,
      // Transfer rate factors
      manager_support: 'informed',
      job_alignment: 'role_relevant',
      time_to_apply: '2_4_weeks',
      learner_population: null,
    };
  });
  
  // Apply profile defaults on mount
  useEffect(() => {
    if (hasProfileDefaults && !profileDefaultsApplied) {
      setConfig(prev => ({
        ...prev,
        elasticity_scenario: profileDefaults.elasticity_scenario,
        intervention_cost: profileDefaults.intervention_cost,
        current_maturity: profileDefaults.current_maturity,
        dampening_factors: profileDefaults.dampening_factors,
        headcount: profileDefaults.headcount,
        revenue_per_employee: profileDefaults.revenue_per_employee,
      }));
      setProfileDefaultsApplied(true);
    }
  }, [hasProfileDefaults, profileDefaults, profileDefaultsApplied]);

  // Auto-adopt the latest Maturity Assessment as the slider's
  // source of truth — same behavior as the Single Measure page.
  // Runs once per page load; user drag still wins for the
  // remainder of the session (revert button restores).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/maturity-assessment/latest');
        if (cancelled) return;
        const a = res?.data?.assessment;
        if (!a || a.overall_score == null) return;
        setLatestMaturityAssessment(a);
        if (!maturityAssessmentApplied && !maturityOverridden && analysisMode !== 'import') {
          setConfig(prev => ({ ...prev, current_maturity: Math.round(a.overall_score) }));
          setMaturityAssessmentApplied(true);
        }
      } catch {
        // Non-fatal — slider falls back to profile default.
      }
    })();
    return () => { cancelled = true; };
  }, [analysisMode, maturityAssessmentApplied, maturityOverridden]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [leiRes, kpiRes] = await Promise.all([
          getLEIMeasures(),
          getKPIs(),
        ]);
        setLEIMeasures(leiRes.data);
        setKPIs(kpiRes.data);
        
        // Initialize measure inputs
        setMeasureInputs(
          leiRes.data.map((m) => ({
            lei_measure_id: m.id,
            lei_improvement: 0,
            enabled: false,
          }))
        );
      } catch (error) {
        logger.error('Error loading data:', error);
        toast.error('Failed to load reference data');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Pre-fill from LEI Reference "Model the fix" hand-off. We wait
  // for measureInputs to populate so we can flip just the measures
  // belonging to the focused pillar (everyone else stays disabled,
  // keeping the analysis tightly scoped). Default uplift = 5%, a
  // conservative starting point for a "what if" projection.
  useEffect(() => {
    if (!focusPillar) return;
    if (focusPillarApplied) return;
    if (leiMeasures.length === 0 || measureInputs.length === 0) return;
    const focusedIds = new Set(
      leiMeasures.filter((m) => m.pillar === focusPillar).map((m) => m.id),
    );
    if (focusedIds.size === 0) return;
    setMeasureInputs((prev) =>
      prev.map((m) => ({
        ...m,
        enabled: focusedIds.has(m.lei_measure_id),
        lei_improvement: focusedIds.has(m.lei_measure_id) ? 5 : 0,
      })),
    );
    setFocusPillarApplied(true);
    // Scroll the focused pillar into view once the section renders.
    setTimeout(() => {
      pillarRefs.current[focusPillar]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 250);
    toast.success(
      `Focused on ${focusPillarLabel || focusPillar} — measures pre-filled at +5% to model your weak link.`,
    );
  }, [focusPillar, focusPillarApplied, leiMeasures, measureInputs.length, focusPillarLabel]);
  
  // Fetch imported baselines when Import mode is selected
  useEffect(() => {
    const fetchImportedBaselines = async () => {
      if (analysisMode === 'import') {
        setIsLoadingBaselines(true);
        try {
          const response = await api.get('/import/data/lei-baselines/latest');
          setImportedBaselines(response.data);
          
          // Auto-fill measure inputs with imported baselines
          if (response.data.baselines) {
            setMeasureInputs(prev => prev.map(m => {
              const baseline = response.data.baselines[m.lei_measure_id];
              if (baseline?.calculated_score) {
                return { ...m, imported_score: baseline.calculated_score };
              }
              return m;
            }));
          }
        } catch (error) {
          logger.error('Failed to fetch imported baselines:', error);
          toast.error('Failed to load imported data. Please check Data Import.');
        } finally {
          setIsLoadingBaselines(false);
        }
      }
    };
    
    fetchImportedBaselines();
  }, [analysisMode]);
  
  // Handle mode change
  const handleModeChange = (newMode) => {
    setAnalysisMode(newMode);
    setIsAdvancedMode(newMode === 'advanced');
  };

  const updateMeasureInput = (measureId, field, value) => {
    setMeasureInputs((prev) =>
      prev.map((m) =>
        m.lei_measure_id === measureId ? { ...m, [field]: value } : m
      )
    );
    if (results) setResultsStale(true);
  };

  const toggleAllMeasures = (enabled) => {
    setMeasureInputs((prev) =>
      prev.map((m) => ({ ...m, enabled, lei_improvement: enabled ? bulkValue : 0 }))
    );
    if (results) setResultsStale(true);
  };

  const openCalculator = (measure) => {
    setSelectedMeasure(measure);
    setCalculatorOpen(true);
  };

  const handleCalculatorApply = (improvement) => {
    const improvementValue = typeof improvement === 'number' ? improvement : 0;
    if (selectedMeasure) {
      updateMeasureInput(selectedMeasure.id, 'lei_improvement', improvementValue);
      updateMeasureInput(selectedMeasure.id, 'enabled', true);
      toast.success(`Applied ${improvementValue.toFixed(1)}% improvement to ${selectedMeasure.name}`);
    }
  };

  const applyBulkValue = () => {
    const value = parseFloat(bulkValue) || 0;
    if (value <= 0) {
      // Enable all measures with 0% - the inline warning will show automatically
      setMeasureInputs((prev) =>
        prev.map((m) => ({ 
          ...m, 
          enabled: true,
          lei_improvement: 0 
        }))
      );
      if (results) setResultsStale(true);
      return;
    }
    setMeasureInputs((prev) =>
      prev.map((m) => ({ 
        ...m, 
        enabled: true,
        lei_improvement: value 
      }))
    );
    if (results) setResultsStale(true);
    toast.success(`Applied ${value}% to all measures`);
  };

  const handleCalculate = async () => {
    if (!hasValidMeasures) {
      toast.error('You must enter at least one LEI measure with improvement > 0% to calculate ROI');
      return;
    }

    setIsCalculating(true);
    try {
      const response = await calculatePortfolio({
        measures: measureInputs,
        ...config,
        organization_id: viewAsOrg?.id || undefined,
        scale_dampening_config: profileDefaults?.scale_dampening_config || null,
        industry_type: profileDefaults?.industry_type || 'other',
        learning_modality: profileDefaults?.primary_learning_modality || 'blended',
        time_lag_config: profileDefaults?.time_lag_config || null,
      });
      setResults(response.data);
      setResultsStale(false); // Clear stale flag
      // Sync with global context for saving
      setGlobalResults(response.data);
      setGlobalConfig({
        ...config,
        lei_improvement: totalImprovement,
        kpi_ids: [config.kpi_id],
      });
      toast.success('Portfolio analysis complete!');
      
      // Scroll to results after a brief delay for render
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      logger.error('Calculation error:', error);
      toast.error('Portfolio calculation failed');
    } finally {
      setIsCalculating(false);
    }
  };

  // Pin the current Portfolio Analysis result to the Quarterly
  // Deck. Mirrors the shape used by the engine action-plan pins
  // (see EngineActionPlanCard.handlePin) so the deck renders a
  // consistent row regardless of origin.
  const handlePinPortfolio = useCallback(async (options = {}) => {
    if (!results || isPinning || isPinned) return;
    setIsPinning(true);
    try {
      // Recompute inline so we don't take an external dep on the
      // derived `enabledCount` / `measureInputs` array identity —
      // both live later in the render body (temporal dead zone)
      // and including them in the useCallback dep array would
      // throw a ReferenceError on first render.
      const enabled = measureInputs.filter((m) => m.enabled);
      const enabledMeasures = enabled
        .filter((m) => m.lei_improvement > 0)
        .slice(0, 3)
        .map((m) => `${m.measure_name || m.id} +${m.lei_improvement}%`)
        .join(', ');
      const enabledCountLocal = enabled.length;
      // Use the handoff context (if any) to tell the CLO what this
      // scenario was originally scoped for. Falls back to a
      // generic "Portfolio analysis" title.
      const originLabel = focusPEComponentLabel
        ? `PE ${focusPEComponentLabel} +${focusPELiftPoints}pt lift`
        : focusMaturityDimension
          ? `Maturity ${focusMaturityDimension} +${focusMaturityLiftPoints} lift`
          : focusROEMetric
            ? `ROE ${focusROEMetric} variance close (+${focusROELiftPoints}pts)`
            : focusROIScenarioName
              ? `ROI ${focusROIScenarioName} +${focusROILiftPoints}pt lift`
              : focusPillarLabel
                ? `LEI ${focusPillarLabel} focus`
                : `${enabledCountLocal} measures`;
      // The origin_engine tells downstream surfaces (the
      // ScenarioComparisonTile especially) which weak link each
      // scenario was scoped to close. Falls back to "portfolio"
      // for ad-hoc scenarios the CLO built from scratch.
      const originEngine = focusPEComponentLabel
        ? 'pe'
        : focusMaturityDimension
          ? 'maturity'
          : focusROEMetric
            ? 'roe'
            : focusROIScenarioName
              ? 'roi'
              : focusPillarLabel
                ? 'lei'
                : 'portfolio';
      await api.post('/quarterly-deck/pins', {
        path: '/portfolio',
        title: `Portfolio scenario: ${originLabel}`,
        description: enabledMeasures
          ? `Modeling: ${enabledMeasures}${enabledCountLocal > 3 ? ` +${enabledCountLocal - 3} more` : ''}.`
          : 'Portfolio analysis scenario.',
        theme: 'portfolio-analysis',
        origin_engine: originEngine,
        origin_label: originLabel,
        stats: [
          { label: 'ROI', value: `${((results.total_roi_percent || 0) / 100).toFixed(1)}x` },
          { label: 'Net benefit', value: formatCurrency(results.total_net_benefit || 0, currency) },
          { label: 'KPI uplift', value: `${(results.total_kpi_delta_percent || 0).toFixed(2)}%` },
          ...(results.lei_impact?.lei_delta != null
            ? [{ label: 'LEI impact', value: `${results.lei_impact.current_lei}→${results.lei_impact.projected_lei} (+${results.lei_impact.lei_delta})` }]
            : []),
          ...(results.lei_impact?.capability
            ? [{ label: 'Capability (PE)', value: `+${results.lei_impact.capability.capability_pct} / PE +${results.lei_impact.capability.pe_pct}` }]
            : []),
          ...(results.payback_months != null
            ? [{ label: 'Payback', value: `${results.payback_months.toFixed(1)} mo` }]
            : []),
        ],
      });
      setIsPinned(true);
      if (!options.silent) {
        toast.success('Scenario pinned to Quarterly Deck');
      }
    } catch (err) {
      logger.error('Portfolio pin failed:', err);
      if (!options.silent) toast.error('Could not pin — try again');
    } finally {
      setIsPinning(false);
    }
  }, [
    results, isPinning, isPinned, measureInputs, currency,
    focusPEComponentLabel, focusPELiftPoints, focusMaturityDimension,
    focusMaturityLiftPoints, focusROEMetric, focusROELiftPoints,
    focusROIScenarioName, focusROILiftPoints, focusPillarLabel,
  ]);

  // Reset the "pinned" flag whenever results are recalculated so
  // the CLO can pin a fresh snapshot after tweaking measures.
  useEffect(() => {
    if (results) {
      setIsPinned(false);
      autoPinFiredRef.current = false;
    }
  }, [results]);

  // Auto-pin once results are in AND the URL / nav state asked
  // for it. Silent (no toast) because the "Model the fix" CTA
  // already fired one on the way here — a second toast feels
  // spammy. The visible confirmation is the Pin button switching
  // to its green "Pinned ✓" state.
  useEffect(() => {
    if (!autoPinRequested || autoPinFiredRef.current) return;
    if (!results || isPinned || isPinning) return;
    autoPinFiredRef.current = true;
    handlePinPortfolio({ silent: true }).then(() => {
      toast.success('Scenario auto-pinned to Quarterly Deck');
    });
  }, [autoPinRequested, results, isPinned, isPinning, handlePinPortfolio]);

  // Group measures by pillar
  const groupedMeasures = leiMeasures.reduce((acc, measure) => {
    if (!acc[measure.pillar]) acc[measure.pillar] = [];
    acc[measure.pillar].push(measure);
    return acc;
  }, {});

  const enabledCount = measureInputs.filter((m) => m.enabled).length;
  const totalImprovement = measureInputs
    .filter((m) => m.enabled)
    .reduce((sum, m) => sum + m.lei_improvement, 0);
  
  // Check if there are valid measures (enabled with improvement > 0)
  const hasValidMeasures = measureInputs.some((m) => m.enabled && m.lei_improvement > 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Multi-Measure Portfolio ROI"
        description="Compare programs across the L&D portfolio."
        theme={__pageTheme}
        icon={Layers}
        testId="portfolio-hero"
        actions={(
          <div className="flex items-center gap-2">
            <AnalysisModeToggle
              mode={analysisMode}
              onModeChange={handleModeChange}
            />
            <Link
              to="/roe-reference#kpi-roi"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-xs font-medium backdrop-blur-sm transition-colors"
              data-testid="portfolio-hero-reference-btn"
            >
              <BookOpen className="h-3.5 w-3.5" /> ROE Reference
            </Link>
          </div>
        )}
      />
    )}>
    <TooltipProvider>
      <div className="px-4 md:px-8 lg:px-12 py-6 space-y-8 animate-fade-in">
        {/* Header */}
        
        {/* LEI Reference focus banner — shown when the user landed
            here from the "Model the fix" CTA. Surfacing it as the
            first banner anchors the analysis in the user's stated
            goal (close their weak link). Clearable so the user can
            broaden the analysis without losing their inputs. */}
        {focusPillar && (
          <div
            className="flex items-center gap-3 p-3 rounded-lg bg-rose-50 border border-rose-200"
            data-testid="portfolio-focus-pillar-banner"
          >
            <div className="p-2 rounded-md bg-rose-100 shrink-0">
              <AlertTriangle className="h-4 w-4 text-rose-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-rose-900">
                <span className="font-semibold">Focused on {focusPillarLabel || focusPillar}</span>
                {' '}— your primary constraint. Measures in this pillar are pre-enabled at{' '}
                <strong>+5% uplift</strong>. Adjust below to model the financial impact of closing this gap.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-rose-700 hover:text-rose-900 hover:bg-rose-100"
              onClick={() => {
                setFocusPillar(null);
                setFocusPillarLabel(null);
              }}
              data-testid="portfolio-focus-pillar-dismiss"
            >
              Clear focus
            </Button>
          </div>
        )}

        {/* PE-weak-link handoff banner — rendered when the user
            arrived via the "Model this fix" CTA on PE Health.
            Softer handoff than the LEI one: we don't pre-enable
            measures (PE components map fuzzily to LEI measures),
            but we surface the stated goal so the CLO knows which
            lift to model. */}
        {focusPEComponentLabel && (
          <div
            className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
            data-testid="portfolio-focus-pe-banner"
          >
            <div className="p-2 rounded-md bg-amber-100 shrink-0">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-amber-900">
                <span className="font-semibold">Modeling a PE {focusPEComponentLabel} +{focusPELiftPoints}pt lift</span>
                {' '}— adjust the L&D measures most tied to {focusPEComponentLabel} (e.g. manager enablement, coaching, on-the-job reinforcement) to see the financial impact of closing this gap.
              </p>
            </div>
          </div>
        )}

        {/* Maturity-weak-link handoff banner — same soft pattern
            as PE. Landed here via the "Model the fix" CTA on the
            Maturity Health page; gives the CLO context for which
            maturity dimension they came to lift. */}
        {focusMaturityDimension && (
          <div
            className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
            data-testid="portfolio-focus-maturity-banner"
          >
            <div className="p-2 rounded-md bg-amber-100 shrink-0">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-amber-900">
                <span className="font-semibold">Modeling a Maturity {focusMaturityDimension} +{focusMaturityLiftPoints} lift</span>
                {' '}— adjust the L&D measures that most directly build {focusMaturityDimension} capability to see the financial impact of closing this gap.
              </p>
            </div>
          </div>
        )}

        {/* ROE-weak-link handoff banner — variance close context. */}
        {focusROEMetric && (
          <div
            className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
            data-testid="portfolio-focus-roe-banner"
          >
            <div className="p-2 rounded-md bg-amber-100 shrink-0">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-amber-900">
                <span className="font-semibold">Closing an ROE {focusROEMetric} variance by +{focusROELiftPoints}pts</span>
                {' '}— adjust the L&D measures most likely to close the modelled-vs-actual gap on {focusROEMetric} to see the financial impact.
              </p>
            </div>
          </div>
        )}

        {/* ROI-weak-link handoff banner — lift a specific scenario's ROI. */}
        {focusROIScenarioName && (
          <div
            className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
            data-testid="portfolio-focus-roi-banner"
          >
            <div className="p-2 rounded-md bg-amber-100 shrink-0">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-amber-900">
                <span className="font-semibold">Lifting ROI on "{focusROIScenarioName}" by +{focusROILiftPoints}pts</span>
                {' '}— adjust the L&D measures most tied to this program's outcomes (capability lift, transfer, manager reinforcement) to model the gain.
              </p>
            </div>
          </div>
        )}

        {/* Profile Defaults Banner */}
        {hasProfileDefaults && profileDefaultsApplied && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="profile-defaults-banner-portfolio">
            <User className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Using your profile defaults.</span>{' '}
                <span className="text-blue-600">Dampening factors, maturity, headcount, and other settings are pre-filled from your profile. You can adjust them below for this analysis.</span>
              </p>
            </div>
            <Link to="/profile">
              <Button variant="ghost" size="sm" className="text-blue-700 hover:text-blue-900 hover:bg-blue-100">
                Edit Profile
              </Button>
            </Link>
          </div>
        )}

        {/* Import Mode Banner */}
        {analysisMode === 'import' && (
          <div className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-200 rounded-lg" data-testid="import-mode-banner-portfolio">
            <FileSpreadsheet className="h-5 w-5 text-teal-600 flex-shrink-0" />
            <div className="flex-1">
              {isLoadingBaselines ? (
                <p className="text-sm text-teal-800">Loading imported baselines...</p>
              ) : importedBaselines?.count > 0 ? (
                <p className="text-sm text-teal-800">
                  <span className="font-medium">Using imported baselines.</span>{' '}
                  <span className="text-teal-600">{importedBaselines.count} measures available. Baseline scores shown next to each measure.</span>
                </p>
              ) : (
                <p className="text-sm text-amber-800">
                  <span className="font-medium">No imported data found.</span>{' '}
                  <span className="text-amber-600">Go to Data Import to upload your LEI baselines first.</span>
                </p>
              )}
            </div>
            <Link to="/data-management">
              <Button variant="ghost" size="sm" className="text-teal-700 hover:text-teal-900 hover:bg-teal-100">
                Data Import
              </Button>
            </Link>
          </div>
        )}

        {/* Target Cohort Selection */}
        <Card className="border-teal-200 bg-gradient-to-r from-teal-50/50 to-emerald-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-lg">Target Cohort</CardTitle>
            </div>
            <CardDescription>
              Select a cohort to track this portfolio analysis against actual performance data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CohortSelector
              selectedCohort={selectedCohort}
              onCohortChange={setSelectedCohort}
              placeholder="Select target cohort (optional)"
              showCreateOption={true}
              className="bg-white"
            />
            {selectedCohort && (
              <p className="text-xs text-teal-600 mt-2 flex items-center gap-1">
                <Users size={12} />
                Projections will be tracked for: {selectedCohort.name}
              </p>
            )}
          </CardContent>
        </Card>

        <div ref={configSectionRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Measure Selection Panel */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">LEI Measures</CardTitle>
                    <CardDescription>
                      Enable measures and set improvement targets
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleAllMeasures(true)}
                      data-testid="enable-all-btn"
                    >
                      Enable All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleAllMeasures(false)}
                      data-testid="disable-all-btn"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
                {/* Bulk Value Apply */}
                <div className="flex items-center gap-2 mt-4 p-3 bg-slate-50 rounded-lg">
                  <Zap className="h-4 w-4 text-teal-600" />
                  <span className="text-sm text-slate-600">Set all to:</span>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={bulkValue}
                    onChange={(e) => setBulkValue(parseFloat(e.target.value) || 0)}
                    className="w-20 h-8 text-center"
                    data-testid="bulk-value-input"
                  />
                  <span className="text-sm text-slate-600">%</span>
                  <Button
                    size="sm"
                    onClick={applyBulkValue}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="apply-bulk-btn"
                  >
                    Apply to All
                  </Button>
                </div>
                
                {/* Validation Warning - shown when measures enabled but all at 0% */}
                {!hasValidMeasures && enabledCount > 0 && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      You must enter at least one LEI measure with improvement greater than 0% to calculate ROI impact.
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {Object.entries(groupedMeasures).map(([pillar, measures]) => {
                    const isFocused = pillar === focusPillar;
                    return (
                      <div
                        key={pillar}
                        ref={(el) => { pillarRefs.current[pillar] = el; }}
                        className={cn(
                          'rounded-lg transition-all',
                          isFocused && 'ring-2 ring-rose-400 ring-offset-2 bg-rose-50/40 p-3',
                        )}
                        data-testid={`portfolio-pillar-group-${pillar.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                          {pillar}
                          {isFocused && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-500 text-white uppercase tracking-wider">
                              <AlertTriangle className="h-2.5 w-2.5" />
                              Largest Value Leak
                            </span>
                          )}
                        </h3>
                        <div className="space-y-2">
                        {measures.map((measure) => {
                          const input = measureInputs.find(
                            (m) => m.lei_measure_id === measure.id
                          );
                          return (
                            <div
                              key={measure.id}
                              className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                                input?.enabled
                                  ? 'bg-teal-50 border-teal-200'
                                  : 'bg-slate-50 border-slate-200'
                              }`}
                            >
                              <Switch
                                checked={input?.enabled || false}
                                onCheckedChange={(checked) =>
                                  updateMeasureInput(measure.id, 'enabled', checked)
                                }
                                data-testid={`toggle-${measure.id}`}
                              />
                              <div className="flex-1 min-w-0 flex items-center gap-2">
                                <p className="font-medium text-slate-900 truncate">
                                  {measure.name}
                                </p>
                                <LEIMeasureInfo measure={measure} iconSize="sm" />
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Calculator Button - visible in advanced mode */}
                                {analysisMode === 'advanced' && measure.lei_formula && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        onClick={() => openCalculator(measure)}
                                        data-testid={`calculator-${measure.id}`}
                                      >
                                        <FlaskConical className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Open LEI Calculator</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {/* Import baseline indicator */}
                                {analysisMode === 'import' && importedBaselines?.baselines?.[measure.id] && (
                                  <span className="text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded-full font-medium" title="Imported baseline score">
                                    {importedBaselines.baselines[measure.id].calculated_score?.toFixed(1)}
                                  </span>
                                )}
                                <Input
                                  type="number"
                                  min="0"
                                  max="20"
                                  step="0.5"
                                  value={input?.lei_improvement || 0}
                                  onChange={(e) =>
                                    updateMeasureInput(
                                      measure.id,
                                      'lei_improvement',
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  disabled={!input?.enabled}
                                  className="w-20 text-center"
                                  data-testid={`improvement-${measure.id}`}
                                />
                                <span className="text-sm text-slate-500">%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Configuration Panel */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Target KPI & Parameters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Target KPI</Label>
                  <Select
                    value={config.kpi_id}
                    onValueChange={(value) =>
                      setConfig((prev) => ({ ...prev, kpi_id: value }))
                    }
                  >
                    <SelectTrigger data-testid="portfolio-kpi-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {kpis.map((kpi) => (
                        <SelectItem key={kpi.id} value={kpi.id}>
                          {kpi.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Elasticity Scenario</Label>
                  <Select
                    value={config.elasticity_scenario}
                    onValueChange={(value) =>
                      setConfig((prev) => ({ ...prev, elasticity_scenario: value }))
                    }
                  >
                    <SelectTrigger data-testid="portfolio-elasticity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservative">Conservative</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="aggressive">Aggressive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Total Intervention Cost ({getCurrencySymbol(currency)})</Label>
                  <Input
                    type="number"
                    value={config.intervention_cost}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        intervention_cost: parseFloat(e.target.value) || 0,
                      }))
                    }
                    data-testid="portfolio-cost-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>Headcount</Label>
                    <Input
                      type="number"
                      value={config.headcount}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          headcount: parseInt(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rev/Employee</Label>
                    <Input
                      type="number"
                      value={config.revenue_per_employee}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          revenue_per_employee: parseFloat(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  {/* Headline + assessment-source indicator —
                      mirrors the Single Measure page so both
                      surfaces feel identical. */}
                  <div className="flex items-baseline gap-3" data-testid="portfolio-maturity-headline">
                    <span className="text-3xl font-extrabold text-slate-900 tracking-tight">
                      {config.current_maturity}%
                    </span>
                    <span className={cn(
                      "text-sm font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full",
                      config.current_maturity <= 25 && "bg-rose-100 text-rose-700",
                      config.current_maturity > 25 && config.current_maturity <= 50 && "bg-amber-100 text-amber-700",
                      config.current_maturity > 50 && config.current_maturity <= 75 && "bg-sky-100 text-sky-700",
                      config.current_maturity > 75 && "bg-emerald-100 text-emerald-700",
                    )}>
                      {config.current_maturity <= 25 ? 'Emerging'
                        : config.current_maturity <= 50 ? 'Developing'
                        : config.current_maturity <= 75 ? 'Managed'
                        : 'Optimized'}
                    </span>
                    <span className="text-xs text-slate-500">value realisation maturity</span>
                  </div>
                  {latestMaturityAssessment && (
                    <div
                      className="inline-flex items-center gap-2 text-[11px] text-slate-600"
                      data-testid="portfolio-maturity-source-indicator"
                    >
                      {maturityOverridden ? (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold uppercase tracking-wide">
                            Manual override
                          </span>
                          <span>
                            Latest assessment scored {Math.round(latestMaturityAssessment.overall_score)}%
                            {latestMaturityAssessment.maturity_band ? ` (${latestMaturityAssessment.maturity_band})` : ''}
                            {' · '}
                            <button
                              type="button"
                              className="text-teal-600 hover:underline font-semibold"
                              onClick={() => {
                                setConfig(prev => ({ ...prev, current_maturity: Math.round(latestMaturityAssessment.overall_score) }));
                                setMaturityOverridden(false);
                              }}
                              data-testid="portfolio-maturity-revert"
                            >
                              Revert to assessment
                            </button>
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold uppercase tracking-wide">
                            Synced from Maturity Assessment
                          </span>
                          <button
                            type="button"
                            className="text-teal-600 hover:underline font-semibold"
                            onClick={() => navigate('/maturity-health')}
                            data-testid="portfolio-maturity-view-assessment"
                          >
                            View assessment
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  <MaturitySliderWithGuide
                    value={config.current_maturity}
                    onChange={(value) => {
                      setConfig((prev) => ({ ...prev, current_maturity: value }));
                      setMaturityOverridden(true);
                    }}
                    showTitle={false}
                    testId="portfolio-maturity-slider"
                  />
                </div>

                {/* Execution Risk Factors */}
                <div className="border-t pt-3">
                  <ExecutionRiskCollapsible
                    values={config.dampening_factors}
                    onChange={(field, value) =>
                      setConfig((prev) => ({
                        ...prev,
                        dampening_factors: { ...prev.dampening_factors, [field]: value }
                      }))
                    }
                    isOpen={showDampeningDetails}
                    onToggle={() => setShowDampeningDetails(!showDampeningDetails)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Value Transfer Conditions */}
            <TransferFactorsCard
              config={config}
              onConfigChange={(updates) => {
                setConfig(prev => ({ ...prev, ...updates }));
                setResultsStale(true);
              }}
              headcount={config.headcount}
              showLearnerPopulation={true}
            />

            <Button
              onClick={handleCalculate}
              disabled={isCalculating || !hasValidMeasures}
              className={`w-full ${hasValidMeasures ? 'bg-teal-600 hover:bg-teal-700' : 'bg-slate-400 cursor-not-allowed'}`}
              size="lg"
              data-testid="calculate-portfolio-btn"
            >
              {isCalculating ? (
                <>
                  <div className="spinner mr-2" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="mr-2 h-5 w-5" />
                  Calculate Portfolio Impact
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Results Section */}
        {results && (
          <div ref={resultsRef} className="space-y-8 animate-fade-in">
            {/* Stale Results Warning */}
            {resultsStale && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Configuration has changed
                  </p>
                  <p className="text-xs text-amber-600">
                    The results below are from your previous calculation. Click "Recalculate" to update.
                  </p>
                </div>
                <Button 
                  onClick={handleCalculate}
                  disabled={isCalculating}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  size="sm"
                >
                  {isCalculating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="mr-2 h-4 w-4" />
                      Recalculate
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {/* ========== SECTION 1: RESULTS ========== */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-teal-600" />
                  <h2 className="text-xl font-bold text-slate-900">Results Summary</h2>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={isPinned ? 'outline' : 'default'}
                    onClick={() => handlePinPortfolio()}
                    disabled={!results || isPinning || resultsStale}
                    className={isPinned
                      ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                      : 'bg-rose-600 hover:bg-rose-700 text-white'
                    }
                    data-testid="portfolio-pin-to-deck-btn"
                    title={resultsStale
                      ? 'Recalculate before pinning to capture the latest numbers'
                      : 'Pin this scenario to your Quarterly Deck'}
                  >
                    {isPinning ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : isPinned ? (
                      <Check className="mr-2 h-4 w-4" />
                    ) : (
                      <Pin className="mr-2 h-4 w-4" />
                    )}
                    {isPinned ? 'Pinned to deck' : 'Pin to Quarterly Deck'}
                  </Button>
                  <SaveScenarioDialog 
                    defaultName={`Portfolio Analysis (${enabledCount} measures)`}
                    analysisType="portfolio"
                    trigger={
                      <Button variant="outline" data-testid="save-portfolio-btn">
                        <Save className="mr-2 h-4 w-4" />
                        Save Scenario
                      </Button>
                    }
                  />
                  <BoardReadyButton 
                    analysisType="portfolio"
                    defaultName={`Portfolio Analysis (${enabledCount} measures)`}
                  />
                </div>
              </div>
              
              {/* Aggregate KPI Cards - Styled like Single Measure */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="h-4 w-4" />
                      <p className="text-sm text-teal-100">Total KPI Uplift</p>
                    </div>
                    <p className="text-3xl font-bold">
                      {(results.total_kpi_delta_percent || 0).toFixed(2)}%
                    </p>
                    <p className="text-xs text-teal-700 mt-1">
                      {results.measure_results?.length || 0} measures combined
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-navy-600 to-navy-700 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4" />
                      <p className="text-sm text-slate-200">Financial Impact</p>
                    </div>
                    <p className="text-3xl font-bold">
                      {formatCurrency(results.total_financial_impact || 0, currency)}
                    </p>
                    <p className="text-xs text-slate-300 mt-1">Annual value</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="h-4 w-4" />
                      <p className="text-sm text-emerald-100">Net Benefit</p>
                    </div>
                    <p className="text-3xl font-bold">
                      {formatCurrency(results.total_net_benefit || 0, currency)}
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">After investment</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <PieChartIcon className="h-4 w-4" />
                      <p className="text-sm text-purple-100">Portfolio ROI</p>
                    </div>
                    <p className="text-3xl font-bold">
                      {((results.total_roi_percent || 0) / 100).toFixed(1)}x
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-4 w-4" />
                      <p className="text-sm text-amber-100">Payback Period</p>
                    </div>
                    <p className="text-3xl font-bold">
                      {results.payback_months != null 
                        ? `${results.payback_months.toFixed(1)} mo`
                        : 'N/A'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Projected LEI impact — the modeled change to the LEI index */}
              {results.lei_impact && (
                <LeiImpactCard leiImpact={results.lei_impact} testId="portfolio-lei-impact" forecastKey="portfolio" />
              )}

              {/* Analysis Notes - Detailed Warnings */}
              {results.warning_details && results.warning_details.length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Analysis Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {results.warning_details.map((detail, idx) => (
                      <div key={detail.type || `warn-${idx}`} className="flex items-start justify-between gap-4">
                        <div className="space-y-2 flex-1">
                          <p className="font-semibold text-amber-800">
                            {results.warnings?.[idx] || detail.type}
                          </p>
                          <p className="text-sm text-amber-700">{detail.explanation}</p>
                          {detail.typical_ranges && (
                            <div className="text-xs bg-white/50 rounded p-2 mt-1">
                              <p className="font-medium text-amber-800 mb-1 underline">Typical ROI Ranges:</p>
                              <div className="space-y-0.5">
                                {Object.entries(detail.typical_ranges).map(([key, val]) => (
                                  <div key={key} className="flex">
                                    <span className="capitalize text-amber-600 w-24">{key}:</span>
                                    <span className="font-mono text-amber-700">{val}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {detail.guidance && detail.guidance.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-amber-800 mb-1">Recommended Actions:</p>
                              <ul className="space-y-1">
                                {detail.guidance.map((g, gIdx) => (
                                  <li key={`${detail.type || 'g'}-${gIdx}-${String(g).slice(0,24)}`} className="text-xs text-amber-700 flex items-start gap-2">
                                    <span className="text-amber-500">→</span>
                                    {g}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={scrollToConfig}
                          className="flex-shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          <Settings className="mr-1 h-4 w-4" />
                          Adjust Parameters
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Monte Carlo Results */}
              <Card className="border-teal-200 bg-teal-50/30">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <CardTitle className="text-lg font-heading">
                      Monte Carlo Simulation (5,000 iterations)
                    </CardTitle>
                    {Math.abs((results.roi_p95 || 0) - (results.roi_p5 || 0)) <= 10 && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-300 bg-teal-100 px-2.5 py-1 text-xs font-semibold text-teal-800" data-testid="portfolio-mc-confidence-badge">
                        <span className="h-2 w-2 rounded-full bg-teal-600" /> High confidence · tight band
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-sm text-slate-500">Mean ROI</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {(results.roi_mean / 100)?.toFixed(1)}x
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-sm text-slate-500">P5 (Downside)</p>
                      <p className="text-2xl font-bold text-rose-600">
                        {(results.roi_p5 / 100)?.toFixed(1)}x
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-sm text-slate-500">P95 (Upside)</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {(results.roi_p95 / 100)?.toFixed(1)}x
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-sm text-slate-500">Prob. Positive</p>
                      <p className="text-2xl font-bold text-teal-600">
                        {results.prob_positive_roi?.toFixed(1)}%
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-sm text-slate-500">Prob. ROI &gt;1x</p>
                      <p className="text-2xl font-bold text-navy-700">
                        {results.prob_roi_over_100?.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ========== SECTION 2: HOW WE GOT HERE ========== */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                <BarChart3 className="h-5 w-5 text-navy-700" />
                <h2 className="text-xl font-bold text-slate-900">How We Got Here</h2>
                <span className="text-sm text-slate-500 ml-2">Impact contribution breakdown</span>
              </div>

              {/* Measure Contribution Charts */}
              <MeasureContributionChart
                measureResults={results.measure_results}
                totalKpiDelta={results.total_kpi_delta_percent}
                totalFinancialImpact={results.total_financial_impact}
              />

              {/* Pie Chart for Share Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <MeasureSharePieChart
                  measureResults={results.measure_results}
                  totalFinancialImpact={results.total_financial_impact}
                  title="Financial Impact Share"
                />
                
                {/* Calculation Notes Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading">Calculation Methodology</CardTitle>
                    <CardDescription className="text-xs">
                      Key factors applied to this analysis
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      {results.calculation_notes?.map((note, idx) => (
                        <div key={`note-${note.slice(0, 20)}-${idx}`} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 flex-shrink-0" />
                          <span className="text-slate-600">{note}</span>
                        </div>
                      ))}
                      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs font-medium text-slate-700">Formula Applied:</p>
                        <p className="text-xs font-mono text-slate-600 mt-1">
                          KPI Delta = LEI% × Weight × Elasticity × Dampening × Maturity
                        </p>
                        <p className="text-xs font-mono text-slate-600 mt-1">
                          Financial Impact = KPI Delta × Baseline × <span className="text-amber-600 font-semibold">Combined Adjustment</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Impact Adjustments Card */}
              {/* Impact Adjustments Card - Collapsible */}
              {results && (
                <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                  <Collapsible open={isImpactAdjustmentsExpanded} onOpenChange={setIsImpactAdjustmentsExpanded}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="pb-2 cursor-pointer hover:bg-amber-100/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                              <User className="h-4 w-4 text-amber-600" />
                              Impact Adjustments Applied
                              {isImpactAdjustmentsExpanded ? (
                                <ChevronUp className="h-4 w-4 text-amber-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-amber-400" />
                              )}
                            </CardTitle>
                            {!isImpactAdjustmentsExpanded && (
                              <CardDescription className="text-sm text-muted-foreground">
                                Click to view factors that adjust theoretical impact
                              </CardDescription>
                            )}
                          </div>
                          {results.combined_adjustment_factor && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-100 text-amber-700">
                              <div className="text-right">
                                <p className="text-lg font-bold">{(results.combined_adjustment_factor * 100).toFixed(1)}%</p>
                                <p className="text-xs font-medium">Combined Adjustment</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Combined Adjustment Factor */}
                          {results.combined_adjustment_factor && (
                            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-300">
                              <div>
                                <p className="text-sm font-medium text-slate-700">Combined Adjustment</p>
                                <p className="text-xs text-slate-500">All factors multiplied</p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-amber-600">
                                  {(results.combined_adjustment_factor * 100).toFixed(1)}%
                                </p>
                                <p className="text-xs text-slate-500">of theoretical impact</p>
                              </div>
                            </div>
                          )}

                          {/* Individual Factors Grid */}
                          <div className="grid grid-cols-2 gap-3">
                            {results.scale_dampening && (
                              <div className="p-3 bg-white rounded-lg border border-slate-200">
                                <div className="flex justify-between items-start mb-1">
                                  <p className="text-xs font-medium text-slate-600">Scale Dampening</p>
                                  <span className="text-sm font-bold text-amber-600">
                                    {(results.scale_dampening.factor * 100).toFixed(0)}%
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{results.scale_dampening.bracket}</p>
                              </div>
                            )}
                            {results.industry_modifier && (
                              <div className="p-3 bg-white rounded-lg border border-slate-200">
                                <div className="flex justify-between items-start mb-1">
                                  <p className="text-xs font-medium text-slate-600">Industry</p>
                                  <span className="text-sm font-bold text-blue-600">
                                    {(results.industry_modifier.modifier * 100).toFixed(0)}%
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{results.industry_modifier.industry}</p>
                              </div>
                            )}
                            {results.learning_modality && (
                              <div className="p-3 bg-white rounded-lg border border-slate-200">
                                <div className="flex justify-between items-start mb-1">
                                  <p className="text-xs font-medium text-slate-600">Learning Modality</p>
                                  <span className="text-sm font-bold text-purple-600">
                                    {(results.learning_modality.factor * 100).toFixed(0)}%
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{results.learning_modality.modality}</p>
                              </div>
                            )}
                            {results.time_lag && (
                              <div className="p-3 bg-white rounded-lg border border-slate-200">
                                <div className="flex justify-between items-start mb-1">
                                  <p className="text-xs font-medium text-slate-600">Time Lag</p>
                                  <span className="text-sm font-bold text-teal-600">
                                    {(results.time_lag.factor * 100).toFixed(0)}%
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{results.time_lag.effective_months} effective months</p>
                              </div>
                            )}
                          </div>

                          {/* Capability Transfer Rate */}
                          {results.transfer_rate && (
                            <TransferRateResultsCard 
                              transferRate={results.transfer_rate} 
                              transferMethod={results.transfer_method}
                              roeCompositeDetail={results.transfer_method === 'roe_composite' ? results.transfer_rate : null}
                              className="mt-4" 
                            />
                          )}

                          {/* Raw vs Adjusted Comparison */}
                          {results.total_financial_impact_raw && results.combined_adjustment_factor < 1 && (
                            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-amber-200">
                              <div className="p-2 bg-white rounded border border-slate-200">
                                <p className="text-xs text-slate-500">Theoretical Impact</p>
                                <p className="text-sm font-mono font-semibold text-slate-400 line-through">
                                  {formatCurrency(results.total_financial_impact_raw, currency)}
                                </p>
                              </div>
                              <div className="p-2 bg-white rounded border border-amber-300">
                                <p className="text-xs text-amber-600">Realistic Impact</p>
                                <p className="text-sm font-mono font-semibold text-amber-700">
                                  {formatCurrency(results.total_financial_impact, currency)}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              )}

              {/* Detailed Measure Breakdown Table - Collapsible */}
              <Card>
                <Collapsible open={isDetailedBreakdownExpanded} onOpenChange={setIsDetailedBreakdownExpanded}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-slate-600" />
                            Detailed Breakdown by Measure
                            {isDetailedBreakdownExpanded ? (
                              <ChevronUp className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            )}
                          </CardTitle>
                          {!isDetailedBreakdownExpanded && (
                            <CardDescription className="text-sm text-muted-foreground">
                              Click to view individual contribution of each LEI measure
                            </CardDescription>
                          )}
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-700">
                          <div className="text-right">
                            <p className="text-xs font-medium">{results.measure_results?.length || 0} Measures</p>
                            <p className="text-lg font-bold">{formatCurrency(results.total_financial_impact, currency)}</p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-3 px-4 font-semibold text-slate-600">
                                LEI Measure
                              </th>
                              <th className="text-left py-3 px-4 font-semibold text-slate-600">
                                Pillar
                              </th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">
                                Improvement
                              </th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">
                                Weight
                              </th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">
                                KPI Delta
                              </th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">
                                Financial Impact
                              </th>
                              <th className="text-right py-3 px-4 font-semibold text-slate-600">
                                % of Total
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.measure_results?.map((mr, idx) => (
                              <tr
                                key={`${mr.lei_measure_id}-${mr.kpi_id || ''}-${idx}`}
                                className={idx % 2 === 0 ? 'bg-slate-50' : ''}
                              >
                                <td className="py-3 px-4 font-medium text-slate-900">
                                  {mr.lei_measure_name}
                                </td>
                                <td className="py-3 px-4 text-slate-600 text-xs">
                                  {mr.pillar}
                                </td>
                                <td className="py-3 px-4 text-right font-mono">
                                  {mr.lei_improvement.toFixed(1)}%
                                </td>
                                <td className="py-3 px-4 text-right font-mono">
                                  {mr.weight.toFixed(2)}
                                </td>
                                <td className="py-3 px-4 text-right font-mono text-teal-600">
                                  {mr.kpi_delta_percent.toFixed(3)}%
                                </td>
                                <td className="py-3 px-4 text-right font-mono font-semibold">
                                  {formatCurrency(mr.financial_impact, currency)}
                                </td>
                                <td className="py-3 px-4 text-right font-mono text-slate-500">
                                  {results.total_financial_impact > 0 
                                    ? ((mr.financial_impact / results.total_financial_impact) * 100).toFixed(1)
                                    : 0}%
                                </td>
                              </tr>
                            ))}
                            <tr className="border-t-2 border-slate-300 bg-navy-50">
                              <td
                                colSpan="4"
                                className="py-3 px-4 font-bold text-navy-700"
                              >
                                TOTAL
                              </td>
                              <td className="py-3 px-4 text-right font-mono font-bold text-teal-600">
                                {results.total_kpi_delta_percent?.toFixed(3)}%
                              </td>
                              <td className="py-3 px-4 text-right font-mono font-bold text-navy-700">
                                {formatCurrency(results.total_financial_impact, currency)}
                              </td>
                              <td className="py-3 px-4 text-right font-mono font-bold text-slate-700">
                                100%
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            </div>

            {/* Tell Me What This Means - AI Narrative */}
            <NarrativeCard
              results={results}
              configuration={{
                ...config,
                measures: measureInputs,
                lei_improvement: measureInputs.filter(m => m.enabled).reduce((sum, m) => sum + (m.lei_improvement || 0), 0),
              }}
              analysisType="portfolio"
              measureName={`${measureInputs.filter(m => m.enabled).length} LEI Measures (Portfolio)`}
              kpiNames={kpis.find(k => k.id === config.kpi_id)?.name || 'Productivity per FTE'}
              currency={currency}
              onNarrativeGenerated={setNarrative}
            />

            {/* Industry Benchmark Comparison */}
            <BenchmarkComparison 
              results={{
                roi_percent: results.total_roi_percent,
                kpi_delta_percent: results.total_kpi_delta_percent,
                payback_months: results.payback_months,
                financial_impact: results.total_financial_impact,
              }}
              configuration={{
                lei_improvement: measureInputs.reduce((sum, m) => sum + (m.lei_improvement || 0), 0) / Math.max(measureInputs.length, 1),
                intervention_cost: measureInputs.reduce((sum, m) => sum + (m.intervention_cost || 0), 0),
              }}
              measures={leiMeasures}
            />
          </div>
        )}

        {/* LEI Calculator Drawer */}
        <LEICalculatorDrawer
          open={calculatorOpen}
          onOpenChange={setCalculatorOpen}
          measure={selectedMeasure}
          currentImprovement={
            selectedMeasure 
              ? measureInputs.find(m => m.lei_measure_id === selectedMeasure.id)?.lei_improvement || 0
              : 0
          }
          onApply={handleCalculatorApply}
        />
      </div>
    </TooltipProvider>
    </PageShell>
  );
};

export default PortfolioAnalysisPage;
